import { useState, useCallback } from 'react';
import s from '../styles/ConfirmationCard.module.scss';

/**
 * Title map for nicer display names per action type
 */
const ACTION_TITLES = {
  create_task: 'Assignment Details',
  bulk_create_task: 'Bulk Assignment',
  assign_essay: 'Essay Assignment',
  create_meeting: 'Meeting Details',
  send_message: 'Message Preview',
  bulk_send_message: 'Message Preview',
};

const ACTION_ICONS = {
  create_task: '📋',
  bulk_create_task: '📋',
  assign_essay: '📝',
  create_meeting: '📅',
  send_message: '✉️',
  bulk_send_message: '✉️',
};

const ACTION_CTA = {
  create_task: 'Create Task',
  bulk_create_task: 'Create Tasks',
  assign_essay: 'Assign Essay',
  create_meeting: 'Schedule Now',
  send_message: 'Send Now',
  bulk_send_message: 'Send Now',
};

/**
 * Editable confirmation card for pending actions.
 * Matches Figma designs for Meeting Details, Assignment Details, Message Preview, etc.
 */
export default function ConfirmationCard({ pendingAction, onConfirm, onCancel }) {
  const { actionType, editableFields, resolvedParams, preview = {} } = pendingAction;

  // Initialise editable form values from the editableFields defaults
  const initialValues = {};
  editableFields.forEach((f) => {
    initialValues[f.key] = f.value;
  });
  const [formValues, setFormValues] = useState(initialValues);
  const [isEditing, setIsEditing] = useState(false);

  const handleChange = useCallback((key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleConfirm = () => {
    // Merge formValues over resolvedParams to produce final parameters
    const params = { ...resolvedParams, ...formValues };
    onConfirm(actionType, params);
  };

  const handleCancel = () => {
    onCancel(actionType);
  };

  const title = ACTION_TITLES[actionType] || 'Action Preview';
  const icon = ACTION_ICONS[actionType] || '⚡';
  const cta = ACTION_CTA[actionType] || 'Confirm';

  return (
    <div className={s.card}>
      {/* Header */}
      <div className={s.header}>
        <span className={s.headerIcon}>{icon}</span>
        <span className={s.headerTitle}>{title}</span>
        <button
          className={`${s.editToggle} ${isEditing ? s.editing : ''}`}
          onClick={() => setIsEditing(!isEditing)}
          title={isEditing ? 'Done editing' : 'Edit fields'}
        >
          {isEditing ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          )}
        </button>
      </div>

      {/* Fields */}
      <div className={s.fields}>
        {isEditing
          ? editableFields.map((field) => (
              <EditableField
                key={field.key}
                field={field}
                value={formValues[field.key]}
                onChange={handleChange}
              />
            ))
          : /* Read-only preview */
            <>
              {/* Show recipient names for send_message / bulk_send_message from preview */}
              {(actionType === 'send_message' || actionType === 'bulk_send_message') && preview.recipient_names && (
                <div className={s.previewRow}>
                  <span className={s.previewLabel}>Recipients :</span>
                  <span className={s.previewValue}>{preview.recipient_names}{preview.recipient_count > 5 ? ` (${preview.recipient_count} total)` : ''}</span>
                </div>
              )}
              {editableFields.map((field) => {
                if (field.key === 'student_ids' && (actionType === 'send_message' || actionType === 'bulk_send_message') && preview.recipient_names) {
                  return null;
                }
                const displayVal = field.key === 'student_ids' && preview.recipient_name
                  ? preview.recipient_name
                  : field.key === 'student_ids' && preview.recipient_names
                    ? preview.recipient_names
                    : formatDisplayValue(field, formValues[field.key]);
                return (
                  <div className={s.previewRow} key={field.key}>
                    <span className={s.previewLabel}>{field.label} :</span>
                    <span className={s.previewValue}>{displayVal}</span>
                  </div>
                );
              })}
            </>}
      </div>

      {/* Actions */}
      <div className={s.actions}>
        <button className={s.cancelBtn} onClick={handleCancel}>
          Cancel
        </button>
        <button className={s.confirmBtn} onClick={handleConfirm}>
          {cta}
        </button>
      </div>
    </div>
  );
}

/**
 * Renders the appropriate input for each editable field type.
 */
function EditableField({ field, value, onChange }) {
  const handleChange = (val) => onChange(field.key, val);

  switch (field.type) {
    case 'text':
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <input
            className={s.fieldInput}
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
          />
        </div>
      );

    case 'textarea':
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <textarea
            className={s.fieldTextarea}
            rows={4}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
          />
        </div>
      );

    case 'date':
    case 'datetime':
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <input
            className={s.fieldInput}
            type={field.type === 'datetime' ? 'datetime-local' : 'date'}
            value={formatDateValue(value, field.type)}
            onChange={(e) => handleChange(e.target.value)}
          />
        </div>
      );

    case 'number':
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <input
            className={s.fieldInput}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleChange(Number(e.target.value))}
          />
        </div>
      );

    case 'dropdown':
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <select
            className={s.fieldSelect}
            value={value == null ? '' : String(value)}
            onChange={(e) => handleChange(e.target.value)}
          >
            {(field.options || []).map((opt) => {
              const optId = typeof opt === 'string' ? opt : String(opt.id);
              const optLabel = typeof opt === 'string' ? opt : opt.label;
              return (
                <option key={optId} value={optId}>
                  {optLabel}
                </option>
              );
            })}
          </select>
        </div>
      );

    case 'multi_select': {
      const options = field.options || [];
      const selected = Array.isArray(value) ? value.map(String) : [];
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <div className={s.multiSelect}>
            {options.map((opt) => {
              const optId = typeof opt === 'string' ? opt : String(opt.id);
              const optLabel = typeof opt === 'string' ? opt : opt.label;
              const isChecked = selected.includes(optId);
              return (
                <label key={optId} className={`${s.chip} ${isChecked ? s.chipActive : ''}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {
                      const next = isChecked
                        ? selected.filter((id) => String(id) !== optId)
                        : [...selected, optId];
                      handleChange(next);
                    }}
                    className={s.chipInput}
                  />
                  {optLabel}
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    case 'toggle':
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <button
            type="button"
            className={`${s.toggle} ${value ? s.toggleOn : ''}`}
            onClick={() => handleChange(!value)}
          >
            <span className={s.toggleThumb} />
          </button>
        </div>
      );

    default:
      return (
        <div className={s.fieldGroup}>
          <label className={s.fieldLabel}>{field.label}</label>
          <input
            className={s.fieldInput}
            type="text"
            value={String(value ?? '')}
            onChange={(e) => handleChange(e.target.value)}
          />
        </div>
      );
  }
}

// ---- helpers ----

function formatDateValue(value, fieldType) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (isNaN(d)) return String(value);
    if (fieldType === 'datetime') {
      return d.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
    }
    return d.toISOString().slice(0, 10); // yyyy-MM-dd
  } catch {
    return String(value);
  }
}

function formatDisplayValue(field, value) {
  if (value == null || value === '') return '—';

  if (field.type === 'multi_select' && Array.isArray(value)) {
    const options = field.options || [];
    const labels = value.map((id) => {
      const idStr = String(id);
      const opt = options.find((o) => (typeof o === 'string' ? o : String(o.id)) === idStr);
      return opt ? (typeof opt === 'string' ? opt : opt.label) : idStr;
    });
    return labels.join(', ') || '—';
  }

  if (field.type === 'dropdown') {
    const options = field.options || [];
    const valueStr = value != null ? String(value) : '';
    const opt = options.find((o) => (typeof o === 'string' ? o : String(o.id)) === valueStr);
    if (opt) return typeof opt === 'string' ? opt : opt.label;
    return valueStr || '—';
  }

  if (field.type === 'toggle') return value ? 'Yes' : 'No';

  if (field.type === 'date' || field.type === 'datetime') {
    try {
      const d = new Date(value);
      if (!isNaN(d)) {
        return field.type === 'datetime'
          ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch {
      /* fall through */
    }
  }

  return String(value);
}
